import type {
  CabinetBranchAccess,
  CabinetDeviceSessionView,
  CabinetMemberView,
} from "@planerius/contracts/cabinet-auth"

import type { ApiProblem } from "../../../api/problem"
import { formatDayMonth, formatTimeOfDay, shiftDate, todayInZone } from "../../../lib/format"

/**
 * ДОСТУП СОТРУДНИКА — СЛОВАМИ (ТЗ 04, Б8–Б10; макет П2, 4-Б10).
 *
 * Карточка отвечает на один вопрос владельца при увольнении: что у человека есть и закрыто
 * ли это. Поэтому каждое поле — фраза, а не код: «все, включая будущие», а не пустой
 * список; «не требуется», а не false. Чистые функции — их проверяет тест, экран только
 * раскладывает строки.
 */

/** «Мастер · только свои записи». */
export function roleWords(member: CabinetMemberView): string {
  return member.roleOwnRecordsOnly ? `${member.roleName} · только свои записи` : member.roleName
}

/**
 * Филиалы. ПУСТОЙ СПИСОК — ЭТО «ВСЕ, ВКЛЮЧАЯ БУДУЩИЕ», и ТЗ требует сказать это словами:
 * подразумеваемое «все» владелец сети читает как «ни одного» — или наоборот.
 */
export function branchesWords(member: CabinetMemberView, branches: readonly CabinetBranchAccess[]): string {
  if (member.branchIds.length === 0) return "все, включая будущие"
  const names = member.branchIds.map((id) => branches.find((branch) => branch.id === id)?.name ?? "филиал вне вашего доступа")
  return names.join(", ")
}

/**
 * Второй фактор: требование роли и то, заведён ли он у человека. «Требуется, не заведён» —
 * не ошибка экрана, а то, что владельцу нужно увидеть: войти такой человек не сможет.
 */
export function mfaWords(member: CabinetMemberView): string {
  if (!member.roleRequiresMfa) return member.mfaEnabled ? "не требуется · заведён" : "не требуется"
  return member.mfaEnabled ? "требуется · заведён" : "требуется · ещё не заведён"
}

/** «сегодня 13:40», «вчера», «12 сентября» — по поясу филиала. */
export function whenWords(instant: string, timeZone: string, now: Date = new Date()): string {
  const today = todayInZone(timeZone, now)
  const day = todayInZone(timeZone, new Date(instant))
  if (day === today) return `сегодня ${formatTimeOfDay(instant, timeZone)}`
  if (day === shiftDate(today, -1)) return "вчера"
  return formatDayMonth(instant, timeZone)
}

/**
 * «iPhone (сегодня 13:40), Chrome на стойке (вчера)». Имя устройства — то, что человек
 * видит у себя в «моих устройствах»; без имени — честное «устройство без названия».
 */
export function sessionsWords(
  sessions: readonly CabinetDeviceSessionView[],
  timeZone: string,
  now: Date = new Date(),
): string {
  if (sessions.length === 0) return "нет"
  return sessions
    .map((session) => {
      const name = session.deviceLabel?.trim() || "устройство без названия"
      return `${name} (${whenWords(session.lastUsedAt ?? session.createdAt, timeZone, now)})`
    })
    .join(", ")
}

/** Состояние строкой и тоном значка. «Работает» — без значка: обычное не выделяется. */
export function statusBadge(
  member: CabinetMemberView,
): { readonly label: string; readonly tone: "warn" | "error" | "gray" } | null {
  switch (member.status) {
    case "invited":
      return { label: "приглашён", tone: "warn" }
    case "suspended":
      return { label: "приостановлен", tone: "error" }
    case "removed":
      return { label: "доступ закрыт", tone: "gray" }
    default:
      return null
  }
}

/** Последний вход — главная колонка списка: по ней видны забытые учётки бывших сотрудников. */
export function lastLoginWords(member: CabinetMemberView, timeZone: string, now: Date = new Date()): string {
  return member.lastLoginAt === null ? "ещё не входил" : `заходил ${whenWords(member.lastLoginAt, timeZone, now)}`
}

/**
 * ИТОГ ЗАКРЫТИЯ ДОСТУПА. Текст макета («Все сеансы Анны завершены… которые она вела»)
 * склоняет имя и выбирает род рукой; машина так не может — имя «Анна К.» или «Саша»
 * просклоняет неверно и угадает не тот род. Поэтому фраза построена без имени и рода,
 * смысл тот же. Отступление записано в docs/design/ВОПРОСЫ-ДИЗАЙНЕРУ.md.
 */
export const REVOKED_TEXT =
  "Доступ закрыт. Все сеансы сотрудника в салоне завершены, включая открытые на телефоне. Записи и история действий сотрудника остались."

/** Карточка уволенного: когда и кто закрыл. */
export function removedWords(member: CabinetMemberView, timeZone: string): string {
  if (member.removedAt === null) return "Доступ закрыт"
  const when = formatDayMonth(member.removedAt, timeZone)
  return member.removedByName === null ? `Доступ закрыт ${when}` : `Доступ закрыт ${when} · закрыл ${member.removedByName}`
}

/**
 * Связь не дошла — запрос мог не уйти вовсе. Для закрытия доступа это главное, что нужно
 * сказать: человек должен ТОЧНО знать, что доступ НЕ закрыт (ТЗ 04, Б10). Повтор безопасен:
 * сервер считает повторное закрытие успехом.
 */
export function isConnectionProblem(problem: ApiProblem): boolean {
  return problem.code === "offline" || problem.code === "timeout"
}

/** Чего нельзя сделать с карточкой — с причиной; null — можно. */
export function denialOf(
  member: CabinetMemberView,
  viewerUserId: string,
  canManage: boolean,
): string | null {
  if (!canManage) return "Менять доступ сотрудников может владелец или управляющий"
  // Сам себе — сервер отказывает так же (members.service.ts); последнего владельца он
  // не даёт снять отдельной проверкой, и её текст экран покажет как есть.
  if (member.userId === viewerUserId) return "Свой доступ здесь не меняют: попросите владельца"
  if (member.status === "removed") return "Доступ уже закрыт. Вернуть человека можно новым приглашением"
  return null
}
